import { config } from '../config';
import { tokenManager } from '../auth/tokenManager';

export type ConsentType =
  | 'terms_of_service'
  | 'privacy_policy'
  | 'ai_assistant'
  | 'age_self_declared_16_plus'
  | 'marketing_email'
  | 'cookies_functional'
  | 'cookies_analytics'
  | 'cookies_marketing'
  | string;

export type ConsentMethod =
  | 'registration_form'
  | 'registration_form_backfill'
  | 'sso_implicit'
  | 'sso_post_signup_modal'
  | 'settings_toggle'
  | 'ai_first_use_modal'
  | 'cookie_banner'
  | 'cookie_banner_backfill'
  | 'manual_request'
  | string;

export interface ConsentRecord {
  id: number;
  user_id: number;
  consent_type: ConsentType;
  version: string;
  granted: boolean;
  method: ConsentMethod;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface ConsentStateEntry {
  granted: boolean;
  version: string;
  method: ConsentMethod;
  at: string | null;
}

export interface ConsentState {
  user_id: number;
  current_versions: Record<string, string>;
  state: Record<string, ConsentStateEntry>;
}

export interface RecordConsentInput {
  consent_type: ConsentType;
  granted: boolean;
  method?: ConsentMethod;
  version?: string;
}

export const consentApi = {
  async getHistory(userId: number): Promise<ConsentRecord[]> {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/user/${userId}/consent/history`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch consent history');
    }
    return response.json();
  },

  async getState(userId: number): Promise<ConsentState> {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/user/${userId}/consent/state`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch consent state');
    }
    return response.json();
  },

  async record(
    userId: number,
    payload: RecordConsentInput
  ): Promise<ConsentRecord> {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/user/${userId}/consent/record`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      throw new Error('Failed to record consent');
    }
    return response.json();
  },
};
